import {
  FormHelperText,
  InputAdornment,
  TextField,
  styled,
  IconButton,
} from "@mui/material";
import React from "react";
import { COLORS } from "../assets/theme";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { TextFieldProps } from "@mui/material";
import { ReactNode } from "react";

import { SxProps, Theme } from "@mui/material";
import CustomLabel from "./CustomLabel";

export type ICustomTextFieldProps = {
  label?: string;
  helperText?: string;
  fullWidth?: boolean;
  size?: "small" | "medium" | "large";
  error?: boolean;
  hidden?: boolean;
  endAdornment?: string;
  bgColor?: string;
  rows?: number;
  minHeight?: string;
  minWidth?: string;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  borderRadius?: string;
  validateOnChange?: boolean;
  highlightLabelStar?: boolean;
  showPasswordToggle?: boolean;
  onValidation?: (error: boolean) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  onBlur?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  sx?: SxProps<Theme>;
} & TextFieldProps;

const StyledTextField = styled(TextField)<{
  $bgColor?: string;
  $isMultiline?: boolean;
  $width?: string;
  $minHeight?: string;
  $minWidth?: string;
  /** Only an explicit minWidth prop survives on phones. */
  $phoneMinWidth?: string;
  $fullWidth?: boolean;
  $borderRadius?: string;
  $hidden?: boolean;
}>(
  ({
    theme,
    $bgColor,
    $isMultiline,
    $width,
    $minHeight,
    $minWidth,
    $phoneMinWidth,
    $fullWidth,
    $borderRadius,
    $hidden,
  }) => ({
    backgroundColor: $bgColor || "transparent",
    width: $fullWidth ? "100%" : $width || "100%",
    minWidth: $minWidth || "250px",
    // The 250px default is for desktop forms; on a phone it pushes fields past
    // the screen edge, so there the field just fills its container.
    [theme.breakpoints.down("sm")]: {
      minWidth: $phoneMinWidth || 0,
      maxWidth: "100%",
    },
    borderRadius: $borderRadius || "12px",
    visibility: $hidden ? "hidden" : "visible",
    "& .MuiOutlinedInput-root": {
      borderRadius: $borderRadius || "12px",
      height: $isMultiline ? "auto" : "40px",
      minHeight: $isMultiline ? $minHeight : "39px",
      "& fieldset": {
        borderColor: "#ccc",
      },
      "&:hover fieldset": {
        borderColor: "#868585",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#868585",
      },
      "&.Mui-focused": {
        outline: "0px solid #868585",
      },
    },
    "& .MuiInputBase-input": {
      padding: "10px 12px",
    },
  })
);

const validateFieldValue = (value: string, type: "email"): boolean => {
  if (type === "email") {
    const emails = value
      .split(";")
      .map((email) => email.trim())
      .filter(Boolean);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return (
      emails.length === 0 || emails.some((email) => !emailRegex.test(email))
    );
  } else {
    return value.trim() === "";
  }
};

const CustomTextField: React.FC<
  ICustomTextFieldProps & { minHeight?: string; minWidth?: string }
> = (props) => {
  const {
    label,
    helperText,
    fullWidth = true,
    size = "small",
    error = false,
    bgColor = "transparent",
    rows,
    multiline = false,
    endAdornment,
    validateOnChange = false,
    onValidation,
    onBlur,
    onChange,
    minHeight,
    hidden = false,
    minWidth,
    startIcon,
    endIcon,
    highlightLabelStar = false,
    showPasswordToggle = false,
    type,
    sx = {},
    ...rest
  } = props;

  const [showPassword, setShowPassword] = React.useState(false);

  const sizeWidthMap: Record<string, string> = {
    small: "250px",
    medium: "368px",
    large: "463px",
  };

  const calculatedMinWidth = minWidth || sizeWidthMap[size] || "100%";

  const handleValidation = (value: string) => {
    const isError = validateFieldValue(value, props.type as "email");
    onValidation?.(isError);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (validateOnChange) {
      handleValidation(value);
    }
    onChange?.(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
  };

  return (
    <div style={{ width: fullWidth ? "100%" : calculatedMinWidth, maxWidth: "100%" }}>
      {label && !hidden && (
        <CustomLabel
          text={label}
          fontSize="14px"
          fontWeight={500}
          color={COLORS.typographyColor}
          highlightLabelStar={highlightLabelStar}
        />
      )}
      <StyledTextField
        size={size}
        fullWidth={fullWidth}
        error={error}
        multiline={multiline || !!rows}
        rows={rows}
        $isMultiline={multiline || !!rows}
        $bgColor={bgColor}
        $width={calculatedMinWidth}
        $minHeight={minHeight}
        $minWidth={calculatedMinWidth}
        $phoneMinWidth={minWidth}
        $fullWidth={fullWidth}
        $hidden={hidden}
        onChange={handleChange}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        InputProps={{
          startAdornment: startIcon && (
            <InputAdornment position="start">{startIcon}</InputAdornment>
          ),
          endAdornment: (
            <>
              {endAdornment}
              {endIcon && (
                <InputAdornment position="end">{endIcon}</InputAdornment>
              )}
              {showPasswordToggle && type === "password" && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )}
            </>
          ),
        }}
        sx={{
          ...sx,
        }}
        type={type === "password" && showPassword ? "text" : type}
        {...rest}
      />
      {!hidden && helperText && (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      )}
    </div>
  );
};

export default CustomTextField;
